import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Alert, Card, Modal, Form, InputNumber, Button, Typography, Tooltip } from "antd";
import AppShell from "../../../components/layout/AppShell";
import StepProgressBar from "../../../components/ui/StepProgressBar";
import {
  getInvoiceByPaymentRequestId,
  createQuote,
  createTransaction,
  createOtcRequest,
  getVaultBalance,
  getUser,
  getBanks,
  getTransactionById,
} from "../../../services/api";
import { useSelector } from "react-redux";
import { formatAmount, getMaxDecimalsForCurrency } from "../../../utils";

const { Text } = Typography;

// ── Country → local currency mapping ─────────────────────────────────────────
const COUNTRY_CURRENCY = {
  US: "USD",
  AE: "AED", ET: "ETB", GH: "GHS", KE: "KES", MW: "MWK",
  MZ: "MZN", NG: "NGN", RW: "RWF", ZA: "ZAR", TZ: "TZS",
  UG: "UGX", ZM: "ZMW", ZW: "ZWL",
};

const STEPS = ["Get Quote", "Payment Status"];

// USDC: 7 decimal places
const fmtUsdc = (n) =>
  formatAmount(n,getMaxDecimalsForCurrency("USDC"));

const PayInvoice = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const userId = useSelector((s) => s.auth.userId);
  const authCountry = useSelector((s) => s.auth.country);
  const quotePermissions = useSelector((s) => s.auth.quotePermissions);
  const isOnrampOfframp = quotePermissions.canOnrampOfframp;
  const localCurrency = COUNTRY_CURRENCY[(authCountry || "").toUpperCase()] || "NGN";

  // ── Data ────────────────────────────────────────────────────────────────
  const [invoice, setInvoice]         = useState(null);
  const [receiverName, setReceiverName] = useState(null);
  const [walletBalance, setWalletBalance] = useState("0");
  const [loading, setLoading]         = useState(true);

  // ── Quote ────────────────────────────────────────────────────────────────
  const [quote, setQuote]             = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError]   = useState(false);
  // fromAmount (USDC) and exchange rate derived directly from the seed quote
  const [seedRate, setSeedRate]       = useState(0);
  const [usdcRequired, setUsdcRequired] = useState(0);  // fromAmount from quote

  // ── Receiver bank ────────────────────────────────────────────────────────
  const [receiverBankId, setReceiverBankId] = useState(null);
  const [, setNoBankWarning]                = useState(false);

  // ── ONRAMP_OFFRAMP deposit instructions ──────────────────────────────────
  const [depositInstructions, setDepositInstructions] = useState(null);
  const [depositLoading, setDepositLoading] = useState(false);
  const [receiverLoading, setReceiverLoading] = useState(false);
  const [localAmount, setLocalAmount] = useState(null); // fromAmount from quote (local currency)
  const [userMessage, setUserMessage] = useState(null);

  // ── Payment ──────────────────────────────────────────────────────────────
  const [confirming, setConfirming]   = useState(false);
  const [step, setStep]               = useState(0);

  // ── OTC ──────────────────────────────────────────────────────────────────
  const [otcModalOpen, setOtcModalOpen] = useState(false);
  const [otcLoading, setOtcLoading]   = useState(false);
  const [otcForm]                     = Form.useForm();
  const [otcHasError, setOtcHasError] = useState(false);
  const otcTargetRate = Form.useWatch("targetRate", otcForm);
  const otcSubmitDisabled =
    otcTargetRate === undefined || otcTargetRate === null || otcTargetRate === "" || otcHasError;

  // ── Load invoice + vault + receiver name + receiver bank ─────────────────
  useEffect(() => {
    if (!userId) return;

    const fetches = isOnrampOfframp
      ? [getInvoiceByPaymentRequestId(id)]
      : [getInvoiceByPaymentRequestId(id), getVaultBalance(userId)];
    Promise.all(fetches)
      .then(([invRes, vaultRes]) => {
        const inv = invRes.data ?? null;
        setInvoice(inv);

        // If invoice is already in progress or paid, jump straight to step 1
        // to prevent re-payment on hard refresh
        if (inv?.status && inv.status !== "created") {
          setStep(1);
          if (inv?.fromUserId) {
            setReceiverLoading(true);
            getUser(inv.fromUserId)
              .then((r) => setReceiverName(r?.data?.name ?? null))
              .catch(() => {})
              .finally(() => setReceiverLoading(false));
          }
          // Transaction already exists but hasn't settled — re-fetch its
          // bank/quote details so the deposit instructions survive a refresh.
          if (inv?.transactionId && (inv.status === "processing" || inv.status === "pending")) {
            setDepositLoading(true);
            getTransactionById(inv.transactionId)
              .then((r) => {
                const txn = r?.data;
                if (!txn) return;
                const bankInfo = txn.depositInstructionBankDetails;
                if (bankInfo) setDepositInstructions(bankInfo);

                const userMsg = txn.referenceNumber;
                if (userMsg) setUserMessage(userMsg);
                const fromAmt = Number(txn.quote?.fromAmount ?? 0);
                if (fromAmt > 0) {
                  if (isOnrampOfframp) setLocalAmount(fromAmt);
                  else setUsdcRequired(fromAmt);
                }
              })
              .catch(() => {
                toast.error("Failed to fetch transaction details. Please refresh the page.");
              })
              .finally(() => setDepositLoading(false));
          }
          return;
        }

        if (vaultRes) {
          const usdc = vaultRes?.data?.balance?.find((b) => b.symbol === "USDC");
          setWalletBalance(usdc?.balance ?? "0");
        }
        if (inv?.fromUserId) {
          // Fetch receiver display name (fromUserId = receiver who raised the invoice)
          getUser(inv.fromUserId)
            .then((r) => setReceiverName(r?.data?.name ?? null))
            .catch(() => {});
          // Fetch receiver's bank account — needed for all flows (offramp destination)
          getBanks(inv.fromUserId)
            .then((r) => {
              const banks = r?.data?.banks ?? [];
              const approvedBank = banks.find((b) => b.status === "APPROVE");
              if (approvedBank) {
                setReceiverBankId(approvedBank.bankId);
              } else {
                setNoBankWarning(true);
              }
            })
            .catch(() => setNoBankWarning(true));
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id, userId, isOnrampOfframp]);

  // ── Seed quote ────────────────────────────────────────────────────────────
  // OFFRAMP mode: toAmount = invoice fiat → returns USDC fromAmount
  // ONRAMP_OFFRAMP mode: toAmount = invoice USD amount → fromAmount = local currency sender sends
  const fetchQuote = useCallback(async (inv, senderUserId, onrampOfframp, lclCurrency) => {
    if (!inv || !senderUserId) return;
    setQuoteLoading(true);
    setQuoteError(false);
    setSeedRate(0);
    setUsdcRequired(0);
    setLocalAmount(null);
    try {
      const params = onrampOfframp
        ? {
            userId:       senderUserId,
            fromCurrency: lclCurrency,
            toCurrency:   "USD",
            toAmount:     String(inv.amount),
            category:     "ONRAMP_OFFRAMP",
            paymentRequestId : id,
          }
        : {
            userId:       senderUserId,
            fromCurrency: "USDC",
            toCurrency:   inv.currency || "USD",
            toAmount:     String(inv.amount),
            category:     "OFFRAMP",
            paymentRequestId : id,
          };
      const res = await createQuote(params);
      const q = res.data;
      setQuote(q);

      const fromAmt = Number(q?.fromAmount ?? q?.source_amount ?? 0);
      const toAmt   = Number(q?.toAmount   ?? q?.dest_amount   ?? inv.amount);
      if (onrampOfframp) {
        const drate = Number(q?.drate ?? 0);
        const dsurcharge = Number(q?.dsurcharge ?? 0);
        if (drate > 0) {
          const brnxRate = drate * (1 + dsurcharge / 100);
          const displayAmt = toAmt * brnxRate;
          if (displayAmt > 0) setLocalAmount(displayAmt);
          setSeedRate(1 / brnxRate);
        } else {
          if (fromAmt > 0) setLocalAmount(fromAmt);
          const rate = fromAmt > 0 && toAmt > 0 ? toAmt / fromAmt : Number(q?.rate ?? 0);
          setSeedRate(rate);
        }
      } else {
        if (fromAmt > 0) setUsdcRequired(fromAmt);
        const rate = fromAmt > 0 && toAmt > 0
          ? toAmt / fromAmt
          : Number(q?.rate ?? q?.forex_rate ?? 0);
        setSeedRate(rate);
      }
    } catch {
      setQuoteError(true);
    } finally {
      setQuoteLoading(false);
    }
  }, []);

  useEffect(() => {
    if (invoice && userId && invoice.status === "created") {
      fetchQuote(invoice, userId, isOnrampOfframp, localCurrency);
    }
  }, [invoice, userId, isOnrampOfframp, localCurrency, fetchQuote]);

  // ── Confirm Quote & Continue ──────────────────────────────────────────────
  const handleConfirm = async () => {
    if (!receiverBankId) {
      toast.error("Receiver bank account not found. Please ask the receiver to add a bank account.");
      return;
    }
    setConfirming(true);
    try {
      if (isOnrampOfframp) {
        // ONRAMP_OFFRAMP: lock quote with local currency → USD
        // Commented for future reference
        // const quoteRes = await createQuote({
        //   userId:       userId,
        //   fromCurrency: localCurrency,
        //   toCurrency:   "USD",
        //   toAmount:     String(invoice.amount),
        //   category:     "ONRAMP_OFFRAMP",
        // });
        const lockedQuote = quote;
        // setQuote(lockedQuote);
        const lockedFrom = Number(lockedQuote?.fromAmount ?? lockedQuote?.source_amount ?? 0);
        const lockedDrate = Number(lockedQuote?.drate ?? 0);
        const lockedDsurcharge = Number(lockedQuote?.dsurcharge ?? 0);
        if (lockedDrate > 0) {
          const brnxRate = lockedDrate * (1 + lockedDsurcharge / 100);
          const displayAmt = Number(invoice.amount) * brnxRate;
          if (displayAmt > 0) setLocalAmount(displayAmt);
        } else if (lockedFrom > 0) {
          setLocalAmount(lockedFrom);
        }

        const txnRes = await createTransaction({
          quoteId:          lockedQuote?.id,
          bankAccountId:    receiverBankId,
          paymentRequestId: id,
        });
        const txnData = txnRes?.data ?? txnRes;
        // depositInstructions may be at different paths depending on API shape
        const instructions =
          txnData?.depositInstructions?.bankDetails ??
          txnData?.depositInstructions ??
          txnData?.bank ?? null;
        setDepositInstructions(instructions);
      } else {
        // OFFRAMP: lock quote with USDC → fiat, pay to receiver bank
        // Commented for future reference
        // const quoteRes = await createQuote({
        //   userId:       userId,
        //   fromCurrency: "USDC",
        //   toCurrency:   invoice.currency || "USD",
        //   toAmount:     String(invoice.amount),
        //   category:     "OFFRAMP",
        // });
        const lockedQuote = quote;
        // setQuote(lockedQuote);
        const lockedFrom = Number(lockedQuote?.fromAmount ?? lockedQuote?.source_amount ?? 0);
        if (lockedFrom > 0) setUsdcRequired(lockedFrom);

        await createTransaction({
          quoteId:          lockedQuote?.id,
          bankAccountId:    receiverBankId,
          paymentRequestId: id,
        });
      }

      setStep(1);
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Payment failed. Please try again."
      );
    } finally {
      setConfirming(false);
    }
  };

  // ── Request OTC ──────────────────────────────────────────────────────────
  const handleRequestOtc = async (values) => {
    const quoteId = quote?.id ?? quote?.quote_id;
    if (!quoteId) {
      toast.error("No quote available. Please wait for the rate to load.");
      return;
    }
    setOtcLoading(true);
    try {
      const res = await createOtcRequest({
        quoteId,
        paymentRequestId: id,
        fromCurrency: localCurrency,
        toCurrency: invoice.currency || "USD",
        requestedAmount: Number(invoice.amount),
        targetRate: Number(values.targetRate),
      });
      const newId = res.data?.otcRequestId;
      toast.success("OTC request submitted!");
      setOtcModalOpen(false);
      otcForm.resetFields();
      navigate(`/sender/otc-requests${newId ? `/${newId}` : ""}`);
    } catch {
      toast.error("Failed to submit OTC request. Please try again.");
    } finally {
      setOtcLoading(false);
    }
  };

  // ── Derived values ────────────────────────────────────────────────────────
  const invoiceAmount = Number(invoice?.amount ?? 0);
  const walletAmt     = Number(walletBalance);

  const usdcToPay    = usdcRequired;
  const receiverGets = invoiceAmount;

  const rateLabel = seedRate > 0
    ? isOnrampOfframp
      ? `1 USD = ${formatAmount(1 / seedRate, getMaxDecimalsForCurrency(localCurrency))} ${localCurrency}`
      : `1 USDC = USD ${formatAmount(seedRate, getMaxDecimalsForCurrency("USD"))}`
    : null;

  const bankDetailRows = [
    ["Bank Name",       depositInstructions?.bankName],
    ["Account Name",    depositInstructions?.accountHolderName ?? depositInstructions?.accountName],
    ["Account No.",     depositInstructions?.accountNumber],
    ["Routing Number",  depositInstructions?.routingNumber],
    ["Payment Method",  depositInstructions?.paymentMethod],
    ["SWIFT Code",      depositInstructions?.swiftCode],
    ["Bank Address",    depositInstructions?.bankAddress],
    ["Reference Number", userMessage],
  ].filter(([, value]) => value);

  const insufficientBalance = !isOnrampOfframp && usdcToPay > 0 && walletAmt < usdcToPay;
  const canConfirm = !confirming && !quoteLoading && !quoteError && !insufficientBalance &&
    (isOnrampOfframp ? (localAmount ?? 0) > 0 : usdcToPay > 0);

  // ── Loading / not-found guards ────────────────────────────────────────────
  if (loading) {
    return (
      <AppShell>
        <div className="animate-pulse max-w-[600px] space-y-4">
          <div className="h-4 bg-gray-200 rounded w-48 mb-6" />
          <div className="h-72 bg-gray-200 rounded-xl" />
        </div>
      </AppShell>
    );
  }

  if (!invoice) {
    console.warn("Invoice not found for ID:", invoice);
    return (
      <AppShell>
        <p className="text-sm text-gray-500">Invoice not found.</p>
      </AppShell>
    );
  }

  const isOtcFeatureEnabled = quote?.otcEnabled || false;

  return (
    <AppShell>
      <div className="max-w-[600px]">
        {/* Step bar */}
        <div className="mb-8">
          <StepProgressBar steps={STEPS} currentStep={step} />
        </div>

        {/* ── Step 0: Exchange Quote ──────────────────────────────────────── */}
        {step === 0 && (
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-5">
              {isOnrampOfframp ? "Payment Quote" : "Exchange Quote"}
            </h2>

            {/* Invoice detail card */}
            <div className="border border-purple-200 bg-purple-50/40 rounded-xl px-4 py-3 mb-5">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  {invoice.invoiceId && (
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-medium text-purple-500 bg-purple-50 border border-purple-200 px-2 py-0.5 rounded-md">
                        Ref: {invoice.invoiceId}
                      </span>
                    </div>
                  )}
                  {invoice.comment ? (
                    <p className="text-sm font-semibold text-gray-800">{invoice.comment}</p>
                  ) : (
                    <p className="text-sm text-gray-400 italic">No description</p>
                  )}
                  <p className="text-xs text-gray-400 mt-0.5">
                    {invoice.dueDate
                      ? `Due ${new Date(invoice.dueDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}`
                      : invoice.createdAt
                      ? `Created ${new Date(invoice.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}`
                      : ""}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-xs text-purple-400 mb-0.5">Invoice Amount</p>
                  <p className="text-xl font-bold text-purple-700">
                    {invoice.currency || "USD"} {formatAmount(invoiceAmount,getMaxDecimalsForCurrency(invoice.currency))}
                  </p>
                </div>
              </div>
            </div>

            {/* Quote amounts — skeleton while loading */}
            {quoteLoading ? (
              <div className="animate-pulse rounded-xl bg-blue-50 border border-blue-100 px-4 py-4 mb-2">
                <div className="flex justify-between">
                  <div className="space-y-1.5">
                    <div className="h-3 bg-blue-200 rounded w-28" />
                    <div className="h-6 bg-blue-200 rounded w-24" />
                  </div>
                  <div className="space-y-1.5 items-end flex flex-col">
                    <div className="h-3 bg-blue-200 rounded w-24" />
                    <div className="h-6 bg-blue-200 rounded w-20" />
                  </div>
                </div>
              </div>
            ) : quoteError ? (
              <div className="flex items-center justify-between bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-2">
                <p className="text-sm text-red-600">Could not fetch exchange rate.</p>
                <button
                  onClick={() => fetchQuote(invoice, userId, isOnrampOfframp, localCurrency)}
                  className="text-sm font-medium text-red-600 underline"
                >
                  Retry
                </button>
              </div>
            ) : (
              <div className="rounded-xl bg-blue-50 border border-blue-100 px-4 py-4 mb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium text-blue-400 mb-1">Invoice Amount</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {invoice.currency || "USD"} {formatAmount(receiverGets,getMaxDecimalsForCurrency(invoice.currency))}
                    </p>
                  </div>
                  <div className="text-right">
                    {isOnrampOfframp ? (
                      <>
                        <p className="text-xs font-medium text-blue-400 mb-1">You Send ({localCurrency})</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {localAmount !== null ? formatAmount(localAmount,getMaxDecimalsForCurrency(localCurrency)) : "—"}
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="text-xs font-medium text-blue-400 mb-1">You Pay (USDC)</p>
                        <p className="text-2xl font-bold text-gray-900 flex items-center justify-end gap-1">
                          <span className="text-base text-gray-400">ⓢ</span>
                          {fmtUsdc(usdcToPay)}
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* OTC rate active banner => Need to work on  */}
            {/* {quote?.otcRateActive && quote?.otcExpiresAt && (
              <Alert
                type="warning"
                showIcon
                title="OTC rate applied."
                description={`Valid until ${new Date(quote.otcExpiresAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}. Complete payment before it expires.`}
                className="mb-3"
              />
            )} */}

            {/* Conversion rate */}
            <div className="flex items-center justify-end mb-5">
              {quoteLoading ? (
                <div className="h-3 w-36 bg-gray-200 rounded animate-pulse" />
              ) : rateLabel ? (
                <p className="text-xs text-gray-400">
                  Conversion Rate:{" "}
                  <span className="font-semibold text-gray-600">{rateLabel}</span>
                </p>
              ) : null}
            </div>

            {/* Wallet balance row — OFFRAMP only */}
            {!isOnrampOfframp && (
              <>
                <div className="flex items-center justify-between border border-gray-200 rounded-xl px-4 py-3 mb-1">
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Wallet Balance</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {fmtUsdc(walletBalance)} USDC
                    </p>
                  </div>
                  <button
                    onClick={() => navigate("/sender/treasury/fund")}
                    className="text-sm font-medium text-brand border border-brand rounded-lg px-3 py-1.5 hover:bg-blue-50 transition-colors flex-shrink-0"
                  >
                    Fund Wallet
                  </button>
                </div>
                {insufficientBalance && !quoteLoading && !quoteError && (
                  <p className="text-xs font-medium text-red-500 mb-5 mt-1 ml-1">
                    Insufficient Balance
                  </p>
                )}
                {!insufficientBalance && !quoteLoading && !quoteError && usdcToPay > 0 && (
                  <p className="text-xs text-gray-400 mb-5 mt-1 ml-1">
                    After payment: {fmtUsdc(Math.max(0, walletAmt - usdcToPay))} USDC remaining
                  </p>
                )}
                {(quoteLoading || quoteError || usdcToPay === 0) && (
                  <div className="mb-5" />
                )}
              </>
            )}
            {isOnrampOfframp && <div className="mb-5" />}

            <button
              onClick={handleConfirm}
              disabled={!canConfirm}
              className="w-full bg-brand text-white text-sm font-semibold py-3 rounded-lg hover:bg-brandHover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {confirming ? "Processing…" : isOnrampOfframp ? "Confirm & Get Bank Details" : "Confirm Quote & Continue"}
            </button>

            {/* OTC negotiation — hidden when OTC rate is already active */}
            {isOtcFeatureEnabled && !quoteLoading && !quoteError && quote && !quote.otcRateActive &&
              (!quote.otcRequestId || (quote.otcStatus !== 'INITIATED' && quote.otcStatus !== 'ACCEPTED')) && (
                <Card size="small" className="mt-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <Typography.Text strong className="!text-sm">Sending a large amount?</Typography.Text>
                      <Typography.Paragraph type="secondary" className="!text-xs !mb-0">
                        Request a negotiated rate from BRNX. You can accept or reject the custom rate before any payment is made.
                      </Typography.Paragraph>
                    </div>
                    <Button className="text-[#0061D3]" onClick={() => setOtcModalOpen(true)}>
                      Request OTC Rate
                    </Button>
                  </div>
                </Card>
            )}
            {quote?.otcStatus === 'INITIATED' && (
              <Alert
                showIcon
                title="OTC request Initiated."
                description="BRNX will negotiate a custom rate offline and share it with you. You can accept or reject before any payment is made."
                className="mt-4"
                type="warning"
                variant="filled"
                action={
                  quote?.otcRequestId && (
                    <Button
                      size="small"
                      type="link"
                      className="text-blue-500 hover:!bg-transparent hover:!text-[#0061D3] hover:!underline"
                      onClick={() => navigate(`/sender/otc-requests/${quote.otcRequestId}`)}
                    >
                      View OTC Request
                    </Button>
                  )
                }
              />
            )}
            {quote?.otcStatus === 'ACCEPTED' && (
              <Alert
                showIcon
                title="OTC request Accepted."
                description="Your requested OTC rate has been accepted. You can proceed with the payment."
                className="mt-4"
                type="success"
              />
            )}
          </div>
        )}

        <Modal
          title="Request OTC Rate"
          open={otcModalOpen}
          onCancel={() => { setOtcModalOpen(false); otcForm.resetFields(); setOtcHasError(false); }}
          footer={
            <div className="flex justify-end gap-2">
              <Button className="hover:!bg-red-500 hover:!text-white hover:border-white " onClick={() => { setOtcModalOpen(false); otcForm.resetFields(); setOtcHasError(false); }} disabled={otcLoading}>
                Cancel
              </Button>
              <Button type="primary" onClick={() => otcForm.submit()} loading={otcLoading} disabled={otcSubmitDisabled}>
                Submit Request
              </Button>
            </div>
          }
        >
          <Typography.Paragraph  className="text-base text-gray-600 mb-4">
            BRNX will negotiate a custom rate offline and share it with you. You can accept or reject before any payment is made.
          </Typography.Paragraph>
          <Form
            form={otcForm}
            layout="vertical"
            onFinish={handleRequestOtc}
            onFieldsChange={() => {
              setOtcHasError(otcForm.getFieldsError().some(({ errors }) => errors.length > 0));
            }}
          >
            <Form.Item
              name="targetRate"
              label={`Your target rate — 1 ${invoice.currency} = ? ${localCurrency}`}
              rules={[
                { required: true, message: "Please enter your target rate" },
                {
                  validator: async (_, value) => {
                    if (value === undefined || value === null || value === "") return;

                    const max = quote?.rate;
                    if (max === undefined || max === null) return;

                    const min = Math.max(0, max - 50);
                    const num = Number(value);
                    if (Number.isNaN(num) || num < min || num > max) {
                      throw new Error(`Rate must be between ${min} and ${max}`);
                    }
                  },
                },
              ]}
            >
              <InputNumber
                step={0.01}
                className="w-full"
                placeholder="e.g. 1300"
                formatter={(value) => (value === undefined || value === null ? "" : `${value}`)}
                parser={(value) => value}
                onKeyDown={(e) => {
                  const val = e.target.value;
                  const isDigit = /^[0-9]$/.test(e.key);
                  if (!isDigit) return;
                  const selectionStart = e.target.selectionStart;
                  const selectionEnd = e.target.selectionEnd;
                  const nextVal =
                    val.slice(0, selectionStart) + e.key + val.slice(selectionEnd);
                  if (!/^\d*\.?\d{0,2}$/.test(nextVal)) e.preventDefault();
                }}
              />
            </Form.Item>
          </Form>
        </Modal>

        {/* ── Step 1: Payment Status ──────────────────────────────────────── */}
        {step === 1 && (
          <div className="bg-white border border-gray-200 rounded-xl p-8 flex flex-col items-center text-center">
            {(() => {
              const isPaid = invoice.status === "success";

              return (
                <>
                  {/* Icon */}
                  <div className="relative mb-6">
                    {isPaid ? (
                      <div className="w-16 h-16 rounded-full flex items-center justify-center bg-green-500">
                        <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    ) : (
                      <div className="w-16 h-16 rounded-full flex items-center justify-center bg-orange-400">
                        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    )}
                  </div>

                  {/* Heading + description */}
                  {isPaid ? (
                    <h2 className="text-xl font-bold text-green-600 mb-8">PAID</h2>
                  ) : (
                    <>
                      <h2 className="text-xl font-bold text-gray-900 mb-2">Transaction Created</h2>
                      <p className="text-sm text-gray-500 mb-6">
                        Please make your {localCurrency} bank transfer using the details below.
                        Your payment will be confirmed once we receive your transfer.
                      </p>
                    </>
                  )}

                  {/* Summary */}
                  {receiverLoading ? (
                    <div className="w-full border border-gray-100 rounded-xl overflow-hidden mb-6 text-left animate-pulse">
                      {Array.from({ length: 4 }).map((_, i) => (
                        <div
                          key={i}
                          className={`flex items-center justify-between px-5 py-3 ${
                            i < 3 ? "border-b border-gray-100" : ""
                          }`}
                        >
                          <div className="h-3 bg-gray-100 rounded w-20" />
                          <div className="h-3 bg-gray-200 rounded w-28" />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="w-full border border-gray-100 rounded-xl overflow-hidden mb-6 text-left">
                      {[
                        ...(invoice.invoiceId ? [["Reference", invoice.invoiceId]] : []),
                        ["Receiver", receiverName || "—"],
                        ["Due date", invoice.dueDate
                          ? new Date(invoice.dueDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
                          : invoice.createdAt
                          ? new Date(invoice.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
                          : "—"],
                        ...(invoice.comment ? [["Description", invoice.comment]] : []),
                        isPaid
                          ? ["Amount", `${invoice.currency || "USD"} ${formatAmount(invoice.amount,getMaxDecimalsForCurrency(invoice.currency))}`]
                          : ["You Send", `${formatAmount(localAmount ?? 0,getMaxDecimalsForCurrency(localCurrency))} ${localCurrency}`],
                      ].map(([label, value], i, arr) => (
                        <div
                          key={label}
                          className={`flex items-center justify-between px-5 py-3 ${
                            i < arr.length - 1 ? "border-b border-gray-100" : ""
                          }`}
                        >
                          <span className="text-sm text-gray-400">{label}</span>
                          <span className="text-sm font-medium text-gray-900">{value}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              );
            })()}

            {/* Bank deposit instructions for ONRAMP_OFFRAMP */}
            {isOnrampOfframp && depositLoading && (
              <div className="w-full border border-gray-200 rounded-xl overflow-hidden mb-6 text-left animate-pulse">
                <div className="px-5 py-3 bg-gray-50 border-b border-gray-100">
                  <div className="h-3.5 bg-gray-200 rounded w-32 mb-2" />
                  <div className="h-3 bg-gray-100 rounded w-40" />
                </div>
                {Array.from({ length: 4 }).map((_, i) => (
                  <div
                    key={i}
                    className={`flex items-center justify-between px-5 py-3 bg-white ${
                      i < 3 ? "border-b border-gray-100" : ""
                    }`}
                  >
                    <div className="h-3 bg-gray-100 rounded w-24" />
                    <div className="h-3 bg-gray-200 rounded w-32" />
                  </div>
                ))}
              </div>
            )}

            {isOnrampOfframp && !depositLoading && depositInstructions && (
              <div className="w-full border border-gray-200 rounded-xl overflow-hidden mb-6 text-left">
                <div className="px-5 py-3 bg-gray-50 border-b border-gray-100">
                  <p className="text-sm font-semibold text-gray-700">Bank Transfer Details</p>
                </div>
                {bankDetailRows.map(([label, value], i, arr) => (
                    <div
                      key={label}
                      className={`flex items-start justify-between px-5 py-3 bg-white ${
                        i < arr.length - 1 ? "border-b border-gray-100" : ""
                      }`}
                    >
                      <span className="text-sm text-gray-500 w-36 flex-shrink-0">{label}</span>
                      {label === "Reference Number" ? (
                          <Text ellipsis = { { tooltip: value }} className="text-sm font-semibold text-gray-900 text-right break-all line-clamp-3">
                            {value}
                          </Text>
                      ) : (
                        <span className="text-sm font-semibold text-gray-900 text-right break-all">{value}</span>
                      )}
                    </div>
                  ))}
              </div>
            )}

            <button
              onClick={() => navigate("/sender/invoices")}
              className="px-8 py-2.5 text-sm font-medium text-brand border border-brand rounded-lg hover:bg-blue-50 transition-colors"
            >
              Back to Invoices
            </button>
          </div>
        )}
      </div>
    </AppShell>
  );
};

export default PayInvoice;
